// src/components/bsm/TraderRoadmap.js
import React, { useState } from 'react';
import {
  GraduationCapIcon,
  CandlestickIcon,
  ShieldCheckIcon,
  ActivityIcon,
  CheckIcon,
  ChevronRightIcon
} from './BsmIcons';

const ROADMAP_STAGES = [
  {
    step: '01',
    id: 'foundation',
    title: 'Market Architecture & Demat Operations',
    tagline: 'Understand the machinery before placing real capital',
    icon: GraduationCapIcon,
    focus: 'Clearing corporations (NSCCL/ICCL), Demat security, market auction mechanics, and order types (Limit, SL, SL-M, Iceberg).',
    drills: [
      'Simulated order placing without slippage errors',
      'Bid-ask depth reading on terminal',
      'Circuit limits & auction handling',
    ],
    outcome: '100% operational confidence; zero accidental fat-finger or order entry mistakes.',
  },
  {
    step: '02',
    id: 'priceaction',
    title: 'High-Probability Price Action',
    tagline: 'Trade what you see on pure price charts, not lagging indicators',
    icon: CandlestickIcon,
    focus: 'Multi-timeframe candlestick anatomy, dynamic support/resistance, liquidity sweeps, and high-volume breakout confirmation.',
    drills: [
      'Scanning 500+ NSE stocks in 20 minutes with our institutional screener',
      'Identifying trap candles vs genuine accumulation zones',
      'Multi-timeframe alignment (Weekly + Daily + 15M)',
    ],
    outcome: 'Objective chart reading ability with strictly defined invalidation levels.',
  },
  {
    step: '03',
    id: 'riskmath',
    title: 'Quantitative Risk & Capital Preservation',
    tagline: 'Survival is rule number one; profits take care of themselves',
    icon: ShieldCheckIcon,
    focus: 'The non-negotiable ≤2% account risk rule, mathematical position sizing, 1:2.5 minimum risk-to-reward ratio, and drawdown recovery tables.',
    drills: [
      'Mandatory pre-trade position sizing calculator check',
      'Portfolio beta balancing across sectors',
      'Hard daily stop-loss discipline drills',
    ],
    outcome: 'Mathematically immune to catastrophic account blowups; steady asymmetric compounding.',
  },
  {
    step: '04',
    id: 'livefloor',
    title: 'Live Floor Execution & Trade Journaling',
    tagline: 'Real market hours practice with mentor oversight',
    icon: ActivityIcon,
    focus: 'Live market execution at our Akurdi trading floor between 9:15 AM - 3:30 PM. End-of-day trade journal audits with NISM certified faculty.',
    drills: [
      'Live execution of intraday and swing setups in active market hours',
      'Daily trade diary post-mortem: entry, exit, psychology score',
      'Overcoming FOMO and revenge trading via structured checklists',
    ],
    outcome: 'Calm, systematic, emotionless execution mirroring institutional trading desks.',
  },
];

export default function TraderRoadmap({ onEnrollClick }) {
  const [activeStage, setActiveStage] = useState(0);
  const current = ROADMAP_STAGES[activeStage];
  const CurrentIcon = current.icon;

  return (
    <section className="bsm-section bsm-roadmap-section">
      <div className="section-head text-center">
        <span className="eyebrow-tag">03 // LEARNING PEDAGOGY</span>
        <h2 className="section-heading">The Trader&apos;s 4-Stage Evolution Roadmap</h2>
        <p className="section-subtext">
          How Brilliant Share Market Academy transforms beginners from anxious retail guessers into systematic, disciplined market operators.
        </p>
      </div>

      <div className="roadmap-container">
        {/* Step Navigation Pills */}
        <div className="roadmap-nav-grid">
          {ROADMAP_STAGES.map((stage, idx) => {
            const IconComponent = stage.icon;
            const isActive = idx === activeStage;
            return (
              <button
                type="button"
                key={stage.id}
                className={`roadmap-step-tab ${isActive ? 'active' : ''}`}
                onClick={() => setActiveStage(idx)}
              >
                <div className="step-tab-num">Stage {stage.step}</div>
                <div className="step-tab-body">
                  <div className="step-tab-icon-row">
                    <IconComponent width={18} height={18} className="step-tab-ico" />
                    <strong>{stage.title}</strong>
                  </div>
                  <span className="step-tab-tagline">{stage.tagline}</span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Active Stage Detailed Card */}
        <div className="roadmap-detail-card">
          <div className="roadmap-card-head">
            <div className="card-stage-pill">
              <CurrentIcon width={18} height={18} />
              <span>Phase {current.step} Curriculum Breakdown</span>
            </div>
            <h3>{current.title}</h3>
            <p className="card-stage-desc">{current.focus}</p>
          </div>

          <div className="roadmap-card-body-grid">
            <div className="roadmap-drills-col">
              <h4>Practical Floor Drills</h4>
              <ul className="roadmap-drills-list">
                {current.drills.map((drill, dIdx) => (
                  <li key={dIdx}>
                    <CheckIcon width={15} height={15} className="drill-check" />
                    <span>{drill}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="roadmap-outcome-col">
              <div className="outcome-box">
                <span className="outcome-label">Graduation Milestone</span>
                <p className="outcome-text">{current.outcome}</p>
              </div>

              <button
                type="button"
                className="btn-roadmap-action"
                onClick={() => onEnrollClick(`Roadmap Stage ${current.step}: ${current.title}`)}
              >
                <span>Attend Free Demo on Stage {current.step}</span>
                <ChevronRightIcon width={16} height={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
